import React, { useMemo, useState } from 'react';
import { Alert, Checkbox, Collapse, Empty, Input, Segmented, Space, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getAccessModules, modulesForPermission, permissionMeta } from '../../assets/utils/accessCatalog';
import { canAccess, isPlatformPermission } from '../../assets/utils/permissions';
import { useAuth } from '../../assets/utils/useAuth';
import AffectsCell from './AffectsCell';
import {
  groupPermissionsByCatalog,
  groupSelectionCount,
  permissionIdOf,
} from './rolePermissionGrouping';
import {
  applicationsForUserType,
  filterPermissionsByApplication,
  hasKnownPortal,
  outOfScopeAssigned,
  portalLabelForUserType,
} from './portalScope';

const { Text, Paragraph } = Typography;

function permissionName(record) {
  return String(record?.name || '');
}

function searchHaystack(values) {
  const raw = values
    .filter((value) => value != null && value !== '')
    .join(' ')
    .toLowerCase();
  const spaced = raw.replace(/[-_./]/g, ' ');
  const compact = spaced.replace(/\s+/g, '');
  return `${spaced} ${compact}`;
}

function matchesQuery(values, query) {
  if (!query) return true;
  const haystack = searchHaystack(values);
  return query
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => {
      const spaced = token.replace(/[-_]/g, ' ');
      const compact = spaced.replace(/\s+/g, '');
      return haystack.includes(spaced) || haystack.includes(compact);
    });
}

/**
 * Build a role by ticking capabilities from the permission catalog.
 *
 * Organised by catalog module (Facilities, Claims, Plans…) — the product area
 * a capability belongs to — and every permission is chosen individually.
 *
 * There is deliberately nothing here that assigns a group at once. The screen
 * switches this replaced did exactly that: each one granted everything behind
 * an access module, and because an access module's permission list answers
 * "who may open this screen" it mixes read with write, so the switch
 * over-granted every time. Which screens a role can open is now shown as a
 * consequence of what was ticked, and is read-only.
 */
export default function RolePermissionAssigner({
  permissions = [],
  assignedKeys = [],
  onChange,
  disabled = false,
  catalogModules = [],
  catalogActions = [],
  /**
   * rbac_roles.user_type. A role is issued to one kind of account, and that
   * account only signs in to one portal — so a Care Admin role has no use for
   * facilities.read_own, which is scoped to "the facility you belong to".
   */
  roleUserType,
}) {
  const { accessModules } = useAuth();
  const [query, setQuery] = useState('');
  const [showAllPortals, setShowAllPortals] = useState(false);
  const q = query.trim().toLowerCase();
  const portalScoped = hasKnownPortal(roleUserType) && !showAllPortals;
  const scope = useMemo(
    () => (portalScoped ? applicationsForUserType(roleUserType) : []),
    [portalScoped, roleUserType],
  );

  const assignedSet = useMemo(() => new Set(assignedKeys.map(String)), [assignedKeys]);

  const assignedNames = useMemo(() => {
    const names = [];
    permissions.forEach((p) => {
      if (assignedSet.has(permissionIdOf(p))) names.push(permissionName(p));
    });
    return names;
  }, [permissions, assignedSet]);

  const previewMap = useMemo(() => {
    const map = {};
    assignedNames.forEach((n) => { map[n] = true; });
    return map;
  }, [assignedNames]);

  // The consequence preview. Derived from what is ticked; never an input.
  const unlocked = useMemo(
    () => getAccessModules().filter((mod) => canAccess(mod.key, previewMap)),
    // accessModules looks unused because getAccessModules() reads the catalog
    // out of module state rather than taking it as an argument. It is a real
    // dependency: an operator-edited access catalog must re-derive this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewMap, accessModules]
  );

  const setAssigned = (nextIds) => {
    onChange?.(Array.from(new Set(nextIds.map(String))));
  };

  const toggleId = (id, checked) => {
    if (!id) return;
    if (checked) setAssigned([...assignedKeys, id]);
    else setAssigned(assignedKeys.filter((k) => String(k) !== String(id)));
  };

  // The catalog is the authority on which portal a capability belongs to —
  // it is editable and shared with the API. permissionMeta's answer is the
  // fallback for anything the catalog has no row for, and for environments
  // where 20260923_module_action_application has not run yet.
  const applicationBySlug = useMemo(() => {
    const map = new Map();
    (catalogActions || []).forEach((a) => {
      const slug = a?.permission_slug
        || (a?.resource && a?.action ? `${a.resource}.${a.action}` : '');
      if (slug && a.application) map.set(slug, a.application);
    });
    return map;
  }, [catalogActions]);

  const enriched = useMemo(
    () => permissions
      // platform.* is CLI-managed and must not be attachable to a role here.
      .filter((p) => !isPlatformPermission(permissionName(p)))
      .map((p) => {
        const name = permissionName(p);
        const meta = permissionMeta(name, p);
        const fromCatalog = applicationBySlug.get(name);
        // 'shared' from the catalog is its default, so it must not override a
        // more specific answer the glossary already has.
        const application = fromCatalog && fromCatalog !== 'shared'
          ? fromCatalog
          : meta.application;
        return { ...p, ...meta, application, idKey: permissionIdOf(p) };
      }),
    [permissions, applicationBySlug]
  );

  // Anything already attached stays visible whatever the filter says: hiding
  // an assigned permission would let a save silently drop it, and it is also
  // the only signal that something was mis-assigned in the first place.
  const strays = useMemo(
    () => outOfScopeAssigned(enriched, scope, assignedSet),
    [enriched, scope, assignedSet]
  );

  const inScope = useMemo(() => {
    const kept = filterPermissionsByApplication(enriched, scope);
    if (!strays.length) return kept;
    const seen = new Set(kept.map((p) => p.idKey));
    return [...kept, ...strays.filter((p) => !seen.has(p.idKey))];
  }, [enriched, scope, strays]);

  const hiddenCount = enriched.length - inScope.length;

  const groups = useMemo(
    () => groupPermissionsByCatalog(inScope, catalogModules, catalogActions),
    [inScope, catalogModules, catalogActions]
  );

  const visibleGroups = useMemo(
    () => groups
      .map((group) => ({
        ...group,
        items: group.items.filter((p) => matchesQuery(
          [p.name, p.displayName, p.display_name, p.description, p.resource, p.action, p.affects],
          q
        )),
      }))
      .filter((group) => group.items.length > 0),
    [groups, q]
  );

  const careAdminScreens = unlocked.filter((m) => m.application === 'care-admin').map((m) => m.label);
  const otherScreens = unlocked.filter((m) => m.application !== 'care-admin').map((m) => m.label);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message={`This role will be able to open ${unlocked.length} screen${unlocked.length === 1 ? '' : 's'}`}
        description={
          unlocked.length ? (
            <Space direction="vertical" size={4}>
              <Text>
                <Text strong>Care Admin: </Text>
                {careAdminScreens.length ? careAdminScreens.join(', ') : 'nothing yet'}
              </Text>
              {otherScreens.length ? (
                <Text type="secondary">Elsewhere: {otherScreens.join(', ')}</Text>
              ) : null}
            </Space>
          ) : (
            'Nothing yet — tick the capabilities this role needs below.'
          )
        }
      />

      {hasKnownPortal(roleUserType) ? (
        <Space wrap align="center">
          <Segmented
            value={showAllPortals ? 'all' : 'portal'}
            onChange={(v) => setShowAllPortals(v === 'all')}
            options={[
              { label: portalLabelForUserType(roleUserType), value: 'portal' },
              { label: 'All portals', value: 'all' },
            ]}
          />
          <Text type="secondary">
            {portalScoped
              ? `Showing what a ${portalLabelForUserType(roleUserType)} account can use${hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}`
              : 'Showing every portal — most of these cannot apply to this role'}
          </Text>
        </Space>
      ) : null}

      {strays.length ? (
        <Alert
          type="warning"
          showIcon
          message={`${strays.length} assigned permission${strays.length === 1 ? '' : 's'} from another portal`}
          description={
            <Space direction="vertical" size={4}>
              <Text>
                Already on this role but outside {portalLabelForUserType(roleUserType)}. Shown
                regardless of the filter so saving cannot silently drop them — untick to remove.
              </Text>
              <Space size={4} wrap>
                {strays.map((p) => (
                  <Tag key={p.idKey} style={{ fontFamily: 'monospace' }}>{p.name}</Tag>
                ))}
              </Space>
            </Space>
          }
        />
      ) : null}

      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search capabilities (e.g. approve, send login, facilities.read)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {visibleGroups.length === 0 ? (
        <Empty
          description={q ? `No capability matches "${query}"` : 'No permissions available to assign'}
        />
      ) : (
        <Collapse
          key={q ? `search:${q}` : 'browse'}
          defaultActiveKey={q ? visibleGroups.map((g) => g.key) : []}
          items={visibleGroups.map((group) => {
            const { on, total } = groupSelectionCount(group, assignedSet);
            return {
              key: group.key,
              label: (
                <Space>
                  <Text strong>{group.label}</Text>
                  <Tag color={on ? 'blue' : 'default'}>{on}/{total}</Tag>
                  {group.uncatalogued ? <Tag color="orange">needs a catalog action</Tag> : null}
                </Space>
              ),
              children: (
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  {group.description ? (
                    <Paragraph type="secondary" style={{ marginBottom: 4 }}>
                      {group.description}
                    </Paragraph>
                  ) : null}
                  {group.items.map((p) => (
                    <Checkbox
                      key={p.idKey}
                      checked={assignedSet.has(p.idKey)}
                      disabled={disabled}
                      onChange={(e) => toggleId(p.idKey, e.target.checked)}
                    >
                      <Space direction="vertical" size={0}>
                        <Text>
                          {p.displayName}
                          {' '}
                          <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {p.name}
                          </Text>
                        </Text>
                        {p.description ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>{p.description}</Text>
                        ) : null}
                        {/* Which screens this one capability opens — the same
                            derivation as the banner, per row. */}
                        <AffectsCell modules={modulesForPermission(p.name)} />
                      </Space>
                    </Checkbox>
                  ))}
                </Space>
              ),
            };
          })}
        />
      )}
    </Space>
  );
}
